import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  MapPin,
  AlertCircle,
  User,
  Briefcase,
  FileText,
} from "lucide-react";
import { message, Modal } from "antd";
import { useDropzone } from "react-dropzone";
import {
  useRoundIssuesStore,
  SeverityLevel,
  IssueStatus,
  AssignTo,
} from "../../../store/RoundIssuesStore";
import { usePremisesStore, useOrgUsersStore } from "../../../store/store";
import { PageLoader } from "../../../components/loader/Loader";
import { useS3Upload } from "../../../utils/s3Client";
import FileUpload from "../../../components/FileUpload/FileUpload";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";

interface Attachment {
  url: string;
  name?: string;
  originalName?: string;
  type?: string;
}

const IssueForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  // Stores
  const {
    currentIssue,
    loading: issueLoading,
    fetchIssueById,
    createIssue,
    updateIssue,
  } = useRoundIssuesStore();

  const {
    allPremises,
    fetchPremises,
    loading: premisesLoading,
  } = usePremisesStore();
  const {
    orgUsersData,
    fetchOrgUsers,
    loading: usersLoading,
  } = useOrgUsersStore();
  const { uploadFileToS3 } = useS3Upload();

  // Local State
  const [formLoading, setFormLoading] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<File[] | null>([]);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    [],
  );
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Document Viewer State
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<{
    file: string | File;
    name: string;
  } | null>(null);

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    severity: SeverityLevel;
    assignTo: AssignTo;
    assignedToUserId: string;
    location: {
      region: string;
      premises: string;
      premisesId: string;
      area: string;
      department: string;
    };
    linkedChecklistItemId: string;
    status: IssueStatus;
  }>({
    title: "",
    description: "",
    severity: "Low",
    assignTo: "Maintenance",
    assignedToUserId: "",
    location: {
      region: "",
      premises: "",
      premisesId: "",
      area: "",
      department: "",
    },
    linkedChecklistItemId: "",
    status: "Open",
  });

  // Fetch Master Data
  useEffect(() => {
    fetchPremises();
    fetchOrgUsers();
  }, [fetchPremises, fetchOrgUsers]);

  // Handle Edit Mode or Pre-fill from Checklist
  useEffect(() => {
    if (isEditMode && id) {
      fetchIssueById(id);
    } else {
      // Pre-fill from Query Params (Raise Issue flow)
      const linkedItem = searchParams.get("linkedChecklistItemId");
      const locationParam = searchParams.get("location");
      const descriptionParam = searchParams.get("description");

      if (linkedItem) {
        setFormData((prev) => ({
          ...prev,
          linkedChecklistItemId: linkedItem,
          description: descriptionParam || prev.description,
          title: descriptionParam
            ? `Issue: ${descriptionParam.substring(0, 50)}...`
            : prev.title,
        }));
      }

      if (locationParam) {
        try {
          const loc = JSON.parse(locationParam);
          setFormData((prev) => ({
            ...prev,
            location: {
              ...prev.location,
              ...loc,
            },
          }));
        } catch (e) {
          console.error("Failed to parse location param", e);
        }
      }
    }
  }, [id, isEditMode, fetchIssueById, searchParams]);

  // Populate form when currentIssue loads (Edit Mode)
  useEffect(() => {
    if (isEditMode && currentIssue) {
      setFormData({
        title: currentIssue.title,
        description: currentIssue.description,
        severity: currentIssue.severity,
        assignTo: currentIssue.assignTo,
        assignedToUserId: currentIssue.assignedToUserId || "",
        location: {
          region: currentIssue.location?.region || "",
          premises: currentIssue.location?.premises || "",
          premisesId: currentIssue.location?.premisesId || "",
          area: currentIssue.location?.area || "",
          department: currentIssue.location?.department || "",
        },
        linkedChecklistItemId: currentIssue.linkedChecklistItemId || "",
        status: currentIssue.status,
      });

      // Populate file list for photos
      if (currentIssue.photoEvidence && currentIssue.photoEvidence.length > 0) {
        const existing = currentIssue.photoEvidence.map((url, index) => ({
          url: url,
          name: `Evidence ${index + 1}`,
        }));
        setExistingAttachments(existing);
      }
    }
  }, [currentIssue, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value as any }));
  };

  const handleLocationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "premisesId") {
      const selectedPremise = allPremises.find(
        (p) => p.id === value || p._id === value,
      );
      setFormData((prev) => ({
        ...prev,
        location: {
          ...prev.location,
          premisesId: value,
          premises: selectedPremise?.premisesName || "",
          region: selectedPremise?.region || prev.location.region,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        location: { ...prev.location, [name]: value },
      }));
    }
  };

  // Drag and Drop Logic
  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles?.length) {
      setFilesToUpload((prev) => [...(prev || []), ...acceptedFiles]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
    },
  });

  const handleRemoveNewFile = (index: number) => {
    setFilesToUpload((prev) => {
      const newFiles = [...(prev || [])];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleRemoveExistingAttachment = (index: number) => {
    setExistingAttachments((prev) => {
      const newAtt = [...prev];
      newAtt.splice(index, 1);
      return newAtt;
    });
  };

  const handlePreviewFile = (attachment: Attachment) => {
    setViewingDoc({
      file: attachment.url,
      name: attachment.name || "Document",
    });
    setViewerOpen(true);
  };

  // Also support previewing new files that are not uploaded yet
  const handlePreviewNewFile = (file: File) => {
    // Create a blob URL for preview
    // Note: In a real scenario you'd handle object URL cleanup
    setViewingDoc({
      file: file,
      name: file.name,
    });
    setViewerOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      // Validation
      const totalEvidenceCount =
        existingAttachments.length + (filesToUpload?.length || 0);

      if (formData.severity === "Critical" && totalEvidenceCount === 0) {
        message.warning(
          "Photo evidence is mandatory for Critical severity issues.",
        );
        setFormLoading(false);
        return;
      }
      if (!formData.title || !formData.assignTo) {
        message.error("Please fill in all required fields.");
        setFormLoading(false);
        return;
      }

      // Upload New Files
      let newUploadedUrls: string[] = [];
      if (filesToUpload && filesToUpload.length > 0) {
        setIsUploading(true);
        // Simulate progress or track real progress if useS3Upload supports it
        setUploadProgress(10);

        const uploadPromises = filesToUpload.map(async (file) => {
          // You might want to rename files here to avoid collisions or structure folders
          return await uploadFileToS3(file);
        });

        const results = await Promise.all(uploadPromises);
        newUploadedUrls = results.filter((url): url is string => !!url);
        setUploadProgress(100);
        setIsUploading(false);

        if (newUploadedUrls.length !== filesToUpload.length) {
          message.warning("Some files failed to upload.");
        }
      }

      // Combine existing URLs and new URLs
      const photoEvidence = [
        ...existingAttachments.map((a) => a.url),
        ...newUploadedUrls,
      ];

      const issueData = {
        ...formData,
        photoEvidence,
      };

      let success;
      if (isEditMode) {
        success = await updateIssue(id!, issueData);
      } else {
        success = await createIssue(issueData);
      }

      if (success) {
        message.success(
          `Issue ${isEditMode ? "updated" : "created"} successfully!`,
        );
        navigate("/safety-facility-rounds/issues");
      } else {
        message.error("Failed to save issue. Please try again.");
      }
    } catch (err) {
      console.error(err);
      message.error("An unexpected error occurred.");
    } finally {
      setFormLoading(false);
      setIsUploading(false);
    }
  };

  if (
    (issueLoading || premisesLoading || usersLoading) &&
    !currentIssue &&
    !formData.title
  ) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              {isEditMode ? "Edit Issue" : "Raise New Issue"}
            </h2>
          </div>

          {/* Form Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <form onSubmit={handleSubmit} id="issue-form" className="space-y-8">
              {/* Main Content Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Details */}
                <div className="lg:col-span-2 space-y-6">
                  {/* General Info Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                      <AlertCircle size={20} className="text-blue-500" />
                      Issue Details
                    </h3>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Issue Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="E.g., Broken Window in Hallway"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={5}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                          placeholder="Describe the issue in detail..."
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Severity Level{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="severity"
                            value={formData.severity}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                          </select>
                          {formData.severity === "Critical" && (
                            <p className="text-xs text-red-500 mt-1">
                              * Photo evidence is required for Critical issues.
                            </p>
                          )}
                        </div>

                        {isEditMode && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Status
                            </label>
                            <select
                              name="status"
                              value={formData.status}
                              onChange={handleInputChange}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                            >
                              <option value="Open">Open</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Resolved">Resolved</option>
                              <option value="Closed">Closed</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Evidence Card */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                      <FileText size={20} className="text-blue-500" />
                      Evidence & Attachments
                    </h3>

                    <FileUpload
                      mode={isEditMode || !id ? "create" : "view"} // "create" allows upload
                      files={filesToUpload}
                      onFilesChange={(files) => setFilesToUpload(files)}
                      existingAttachments={existingAttachments}
                      onRemoveExistingAttachment={
                        handleRemoveExistingAttachment
                      }
                      onPreviewFile={(att) => handlePreviewFile(att)}
                      uploading={isUploading}
                      uploadProgress={uploadProgress}
                      getRootProps={getRootProps}
                      getInputProps={getInputProps}
                    />
                  </div>
                </div>

                {/* Right Column: Meta & Location */}
                <div className="space-y-6">
                  {/* Assignment */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                      <User size={20} className="text-blue-500" />
                      Assignment
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Assign By Team <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Briefcase
                            size={18}
                            className="absolute left-3 top-3 text-gray-400 pointer-events-none"
                          />
                          <select
                            name="assignTo"
                            value={formData.assignTo}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white appearance-none"
                          >
                            <option value="Maintenance">Maintenance</option>
                            <option value="Safety">Safety</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Assign To Person
                        </label>
                        <div className="relative">
                          <User
                            size={18}
                            className="absolute left-3 top-3 text-gray-400 pointer-events-none"
                          />
                          <select
                            name="assignedToUserId"
                            value={formData.assignedToUserId}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white appearance-none"
                          >
                            <option value="">Select Person</option>
                            {orgUsersData?.map((user: any) => (
                              <option
                                key={user._id || user.id}
                                value={user._id || user.id}
                              >
                                {user.firstName} {user.lastName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                      <MapPin size={20} className="text-blue-500" />
                      Location
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Premises
                        </label>
                        <select
                          name="premisesId"
                          value={formData.location.premisesId}
                          onChange={handleLocationChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                        >
                          <option value="">Select Premises</option>
                          {allPremises.map((p) => (
                            <option key={p.id || p._id} value={p.id || p._id}>
                              {p.premisesName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Region
                        </label>
                        <input
                          type="text"
                          name="region"
                          value={formData.location.region}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 outline-none"
                          placeholder="Region"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Area / Floor
                        </label>
                        <input
                          type="text"
                          name="area"
                          value={formData.location.area}
                          onChange={handleLocationChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="e.g. Floor 2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Department
                        </label>
                        <input
                          type="text"
                          name="department"
                          value={formData.location.department}
                          onChange={handleLocationChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="e.g. IT Dept"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            {/* Form Actions */}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-2 bg-[#61748e] border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={formLoading}
              form="issue-form"
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {formLoading ? (
                <span>Saving...</span>
              ) : (
                <>
                  <Save size={20} />
                  {isEditMode ? "UPDATE" : "SUBMIT"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      <Modal
        open={viewerOpen}
        onCancel={() => setViewerOpen(false)}
        footer={null}
        width={1000}
        centered
        className="document-viewer-modal"
        styles={{ body: { padding: 0, height: "80vh", overflow: "hidden" } }}
      >
        <div className="h-full flex flex-col bg-gray-50">
          <div className="p-4 border-b flex justify-between items-center bg-white">
            <h3 className="font-semibold text-lg">{viewingDoc?.name}</h3>
          </div>
          <div className="flex-1 overflow-auto p-4 flex justify-center">
            {viewingDoc && (
              <DocumentViewer file={viewingDoc.file} name={viewingDoc.name} />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IssueForm;
